import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type ConditionalAmountNodeParameters = {
    description: string
    amount: number
    condition: ParameterValue<boolean, PricingContext> //any boolean node; VariantConditionNode is deprecated
}

const conditionalAmountNodeDefinition = {
    nodeClass: "ConditionalAmountNode",
    validation: {
        parameters: z.object({
            description: z.string(),
            amount: z.number(),
            condition: parameterValue(z.boolean()),
        }),
        returns: z.number(),
    },
} satisfies GraphNodeDefinition<number, ConditionalAmountNodeParameters>

@registerPricingGraphNode
export class ConditionalAmountNode extends PricingNode<number, ConditionalAmountNodeParameters> {
    public override get definition() {
        return conditionalAmountNodeDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        // an empty VariantConditionNode means that the condition was not set up yet. In contrast to the priced item node, this node is assumed to be always conditional.
        if (this.parameters.condition instanceof VariantConditionNode && this.parameters.condition.getVariantIds().length === 0) return 0
        const condition = await evaluator.get(this.parameters.condition)
        return condition ? this.parameters.amount : 0
    }

    addDependency(configGroupNode: ConfigurationGroupNode, variantId: string, variantName: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.addDependency(configGroupNode, variantId, variantName)
    }

    removeDependency(variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.removeDependency(variantId)
    }

    canAddDependency(groupId: string, variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) return false
        return this.parameters.condition.canAddDependency(groupId, variantId)
    }

    getVariantIds(): string[] {
        if (!(this.parameters.condition instanceof VariantConditionNode)) return []
        return this.parameters.condition.getVariantIds()
    }
}
